
import {
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Button,
    Col,
    Label
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react"
import { httpRequest } from "utils/httpRequest";
import TextEditor from "components/editor/TextEditor";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { useRef } from "react";

const EditSrb = () => {
    const [imagePreview, setImagePreview] = useState(null);
    const [imageLink, setImageLink] = useState("")
    const [isNewImage, setIsNewImage] = useState(false); // Track if user uploaded a new image
    const [stateCityData, setStateCityData] = useState([]);
    const [cities, setCities] = useState([]);
    const [formInitialized, setFormInitialized] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const { id } = useParams();
    const [uploadLoading, setUploadLoading] = useState(false);

    const navigate = useNavigate()
    const prevSelectedState = useRef("");

    // Form validation schema using Yup
    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        description: Yup.string()
            .test('not-empty', 'Description is required', function (value) {
                // Remove HTML tags and check if there's actual content
                const textContent = value ? value.replace(/<[^>]*>/g, '').trim() : '';
                return textContent.length > 0;
            })
            .required("Description is required"),
        image: Yup.mixed().required("Image is required"),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            state: "",
            city: "",
            description: "",
            image: null,
            imagePreview: ""
        },
        validationSchema,
        onSubmit: async (values) => {
            // Manually trigger validation for all fields
            formik.setTouched({
                name: true,
                state: true,
                city: true,
                description: true,
                image: true
            });

            // Wait for validation to complete
            const errors = await formik.validateForm(values);

            // Check if description is empty (after removing HTML tags)
            const descriptionText = values.description ? values.description.replace(/<[^>]*>/g, '').trim() : '';
            if (!descriptionText) {
                formik.setFieldError('description', 'Description is required');
                return false;
            }

            if (Object.keys(errors).length > 0) {
                console.log('Validation errors:', errors);
                return false;
            }

            try {
                let finalImageLink = imageLink;


                if (isNewImage && values.image instanceof File) {
                    finalImageLink = await getUploadedImageLink(values.image);
                } else if (!isNewImage && typeof values.image === 'string') {
                    finalImageLink = values.image; // Use existing image path
                }

                const response = await httpRequest(
                    `associate/update/${id}`,
                    "PUT",
                    {
                        name: values?.name,
                        state: values?.state,
                        city: values?.city,
                        description: values?.description,
                        image: finalImageLink
                    },
                    {},
                    true,
                    true
                );

                if (response?.code === 200) {

                    toast.success("Associate srb updated successfully")
                    formik.resetForm()
                    navigate("/admin/associate-srb")
                }

            } catch (error) {
                console.log(error);

            }
        },
    });

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Create preview URL for new image
        const previewUrl = URL.createObjectURL(file);

        // Update formik values
        formik.setValues({
            ...formik.values,
            image: file,  // Store the file object
            imagePreview: previewUrl
        });

        // Set flags
        setIsNewImage(true);
        setImagePreview(previewUrl);

        try {
            setUploadLoading(true);
            const uploadedImageLink = await getUploadedImageLink(file);
            setImageLink(uploadedImageLink);
        } catch (error) {
            console.log("Upload failed:", error);
            formik.setFieldValue("image", null);
            formik.setFieldError("imagePreview", "");
            toast.error("Image upload failed. Please try again.");
        } finally {
            setUploadLoading(false);
        }
    };

    const getUploadedImageLink = async (file) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await httpRequest(
                "admin/UploadMedia",
                "POST",
                formData,
                {},
                true,
                true
            );
            return response?.filePath;
        } catch (error) {
            console.log("Upload error:", error);
            throw error;
        }
    };

    const getSrbEditData = async () => {
        setIsLoadingData(true);
        try {
            const response = await httpRequest(
                `associate/get/${id}`,
                "GET",
                {},
                {},
                true,
                false
            );

            if (response?.code === 200) {
                const data = response?.data;

                const selectedState = data?.state;
                const selectedCity = data?.city;

                const foundState = stateCityData.find(
                    (s) => s.name.toLowerCase() === selectedState?.toLowerCase()
                );

                if (foundState) {
                    setCities(foundState.cities); // ensures dropdown includes selected city
                } else {
                    setCities([]);
                }

                prevSelectedState.current = data?.state || "";

                // Reset form with existing data
                formik.resetForm({
                    values: {
                        name: data?.name || "",
                        state: data?.state || "",
                        city: data?.city || "",
                        description: data?.description || "",
                        image: data?.image || null,
                        imagePreview: data?.image || ""
                    }
                });


                // Set preview for existing image
                if (data?.image) {
                    const existingImageUrl = `${process.env.REACT_APP_IMAGE_URL}${data.image}`;
                    setImagePreview(existingImageUrl);
                    setIsNewImage(false);
                }

                setFormInitialized(true); // ✅ mark form as load
            }

        } catch (error) {
            console.log(error);
        } finally {
            setIsLoadingData(false);
        }
    }

    // Helper function to get the correct image URL for display
    const getImageDisplayUrl = () => {
        if (isNewImage && formik.values.imagePreview) {
            // For new uploaded images (blob URLs)
            return formik.values.imagePreview;
        } else if (!isNewImage && formik.values.image) {
            // For existing images from server
            return `${process.env.REACT_APP_IMAGE_URL}${formik.values.image}`;
        }
        return null;
    };

    useEffect(() => {
        if (id && stateCityData.length > 0) {
            getSrbEditData();
        }
    }, [id, stateCityData]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            if (isNewImage && formik.values.imagePreview) {
                URL.revokeObjectURL(formik.values.imagePreview);
            }
        };
    }, [isNewImage, formik.values.imagePreview]);





    useEffect(() => {
        fetchStateAndCityList()
    }, [])

    useEffect(() => {
        // Skip if we're still loading initial data
        if (isLoadingData) return;

        const selected = stateCityData.find(
            (s) => s.name.toLowerCase() === formik.values.state.toLowerCase()
        );

        if (selected) {
            setCities(selected.cities);
        } else {
            setCities([]);
        }

        // Only reset city if state actually changed and not during initial load
        if (
            formInitialized &&
            !isLoadingData &&
            prevSelectedState.current !== formik.values.state &&
            prevSelectedState.current !== ""
        ) {
            formik.setFieldValue("city", "");
        }

        if (formik.values.state) {
            prevSelectedState.current = formik.values.state;
        }
    }, [formik.values.state, stateCityData, formInitialized, isLoadingData]);







    const fetchStateAndCityList = async () => {
        try {
            const response = await httpRequest(
                "video/state-city-list",
                "GET",
                {},
                {},
                true,
                false
            );
            console.log("ree", response)
            setStateCityData(response);
        } catch (error) {
            console.log(error)
        }


    }







    return (
        <Col lg="8" md="8" className="mx-auto mt-4">
            <Card className="bg-secondary shadow border-0">
                <CardHeader className="bg-transparent pb-3">
                    <div className="text-muted text-center mt-2 mb-3">
                        <small>Edit Associate SRB</small>
                    </div>
                </CardHeader>
                <CardBody className="px-lg-5 py-lg-4">
                    <Form onSubmit={formik.handleSubmit}>
                        {/* Name */}
                        <FormGroup className="mb-3">
                            <InputGroup className="input-group-alternative">
                                <InputGroupAddon addonType="prepend">
                                    <InputGroupText>
                                        <i className="ni ni-single-02" />
                                    </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                    placeholder="Name"
                                    type="text"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.name && formik.errors.name ? true : false}
                                    disabled={formik.isSubmitting}
                                />
                            </InputGroup>
                            {formik.touched.name && formik.errors.name ? (
                                <div className="text-danger small mt-1">{formik.errors.name}</div>
                            ) : null}
                        </FormGroup>

                        {/* State */}
                        <FormGroup className="mb-3">
                            <InputGroup className="input-group-alternative">
                                <InputGroupAddon addonType="prepend">
                                    <InputGroupText>
                                        <i className="ni ni-world" />
                                    </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                    type="select"
                                    name="state"
                                    value={formik.values.state}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.state && !!formik.errors.state}
                                    disabled={formik.isSubmitting}
                                >
                                    <option value="">Select State</option>
                                    {stateCityData?.map((state) => (
                                        <option key={state.name} value={state.name}>
                                            {state.name}
                                        </option>
                                    ))}
                                </Input>
                            </InputGroup>
                            {formik.touched.state && formik.errors.state && (
                                <div className="text-danger small mt-1">{formik.errors.state}</div>
                            )}
                        </FormGroup>

                        {/* City */}
                        <FormGroup className="mb-3">
                            <InputGroup className="input-group-alternative">
                                <InputGroupAddon addonType="prepend">
                                    <InputGroupText>
                                        <i className="ni ni-pin-3" />
                                    </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                    type="select"
                                    name="city"
                                    value={formik.values.city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.city && !!formik.errors.city}
                                    disabled={cities.length === 0 || formik.isSubmitting}
                                >
                                    <option value="">Select City</option>
                                    {cities.map((city) => (
                                        <option key={city._id} value={city.name}>
                                            {city.name}
                                        </option>
                                    ))}
                                </Input>
                            </InputGroup>
                            {formik.touched.city && formik.errors.city && (
                                <div className="text-danger small mt-1">{formik.errors.city}</div>
                            )}
                        </FormGroup>

                        {/* Description */}
                        <FormGroup>
                            <Label className="form-control-label">Article Description</Label>
                            <TextEditor
                                value={formik.values.description}
                                onChange={(html) => {
                                    formik.setFieldValue("description", html);
                                    // Trigger validation immediately when content changes
                                    formik.setFieldTouched("description", true);
                                }}
                                onBlur={() => formik.setFieldTouched("description", true)}
                                placeholder="Write the article description..."
                                hideImageAndLink={false}
                            />
                            {formik.touched.description && formik.errors.description && (
                                <div className="text-danger small mt-1">
                                    {formik.errors.description}
                                </div>
                            )}
                        </FormGroup>

                        {/* Image Upload */}
                        <FormGroup>
                            <label className="form-control-label">Upload Image</label>
                            <Input
                                type="file"
                                name="image"
                                onChange={handleImageChange}
                                accept="image/*"
                                onBlur={formik.handleBlur}
                                invalid={formik.touched.image && formik.errors.image ? true : false}
                                disabled={formik.isSubmitting}
                            />
                            {formik.touched.image && formik.errors.image ? (
                                <div className="text-danger small mt-1">{formik.errors.image}</div>
                            ) : null}
                        </FormGroup>

                        {/* Image Preview */}
                        {getImageDisplayUrl() && (
                            <div className="mt-3">
                                <img
                                    src={getImageDisplayUrl()}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "25%",
                                        height: "auto",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #ddd"
                                    }}
                                    onError={(e) => {
                                        console.error("Image failed to load:", e.target.src);
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div className="small text-muted mt-1">
                                    {uploadLoading && <i className="fas fa-spinner fa-spin" /> }
                                    {isNewImage ? "New image selected" : "Current image"}
                                </div>
                            </div>
                        )}

                        <div className="text-center">
                            <Button className="my-4" color="primary" disabled={formik.isSubmitting || uploadLoading} type="submit">
                                Update
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </Col>
    );
};

export default EditSrb;